import * as React from "react"

import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { FieldItems, Input, ReadOnly } from "@tenforce/toolbox-field-react"
import { Modal } from "@tenforce/toolbox-modal-react"
import {
    TableBody,
    TableBodyRowContentCell,
    RowContentCell,
} from "@tenforce/toolbox-table-react"

import EditableSiblings from "./EditableSiblings"
import SiblingRow from "./SiblingRow"
import { Symbols } from "@tenforce/toolbox-fontmap"

import "./Siblings.scss"
import { ISibling } from "../index"

interface Props {
    smartLocationFilterLabel: string
    loadMoreLabel: string
    noResultsLabel: string
    siblingsContent?: ISibling[]
    editableSiblings: ISibling[]
    notEditableSiblings: ISibling[]
    filter?: string
    hasMore: boolean
    isLoading: boolean
    isLoadingMore: boolean
    filterSiblings: (filter: string) => void
    loadMoreSiblings: (lastSiblingId: string) => void
    removeSibling: (selectionFilterId: string) => void
    reorderSiblings: (
        targetLocation: number,
        oldLocation: number,
        reorderedSiblingId: string,
        targetSiblingId: string,
    ) => void
}

interface State {
    show: boolean
}

export default class Siblings extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            show: false,
        }
    }

    render(): JSX.Element {
        const {
            smartLocationFilterLabel,
            editableSiblings,
            notEditableSiblings,
            filter,
            hasMore,
            isLoading,
            isLoadingMore,
            filterSiblings,
            removeSibling,
            reorderSiblings,
            loadMoreLabel,
            noResultsLabel,
        } = this.props
        const { show } = this.state

        const hasEditableSiblings = editableSiblings.length > 0
        const hasNotEditableSiblings = notEditableSiblings.length > 0

        return (
            <>
                <Action
                    id="relativeSiblingsButton"
                    extraClasses="tw-mr-1 is-inverted"
                    onClick={this.showModal}
                    isLabel={true}
                >
                    {Symbols.Siblings}
                </Action>

                <Modal
                    isShown={show}
                    containerProps={{
                        id: "relativeSiblingsModal",
                        targetSelector: "#relativeSiblingsButton",
                        onClose: this.hideModal,
                    }}
                >
                    <FieldItems extraClasses="mb-2" isInverted={true}>
                        <Input
                            autoFocus={true}
                            id="relativeSiblingsFilter"
                            type="search"
                            value={filter ?? ""}
                            onChangedValue={filterSiblings}
                            placeholder={smartLocationFilterLabel}
                        />
                    </FieldItems>

                    {isLoading && (
                        <TableBodyRowContentCell
                            extraClasses="tw-pb-2"
                            rowProps={{
                                id: "loadingRow",
                                extraClasses: "is-loading",
                            }}
                        >
                            <Action actionType={ActionType.block}></Action>
                        </TableBodyRowContentCell>
                    )}

                    {!isLoading && (
                        <>
                            {hasNotEditableSiblings && (
                                <TableBody
                                    extraClasses={`${
                                        hasEditableSiblings ? "tw-pb-2" : ""
                                    }`}
                                    id="notEditableRelativeSiblings"
                                >
                                    {notEditableSiblings.map(
                                        (sibling, index) => (
                                            <SiblingRow
                                                sibling={sibling}
                                                key={index}
                                                removeSibling={removeSibling}
                                            />
                                        ),
                                    )}
                                </TableBody>
                            )}

                            <TableBody id="editableRelativeSiblings">
                                {hasEditableSiblings && (
                                    <EditableSiblings
                                        siblings={editableSiblings}
                                        reorderSiblings={reorderSiblings}
                                        removeSibling={removeSibling}
                                        isReorderingDisabled={!!filter}
                                    />
                                )}

                                {hasMore && (
                                    <RowContentCell>
                                        <Action
                                            id="relativeSiblingLoadMore"
                                            isLoading={isLoadingMore}
                                            onClick={this.loadMore}
                                            extraClasses="tw-px-3"
                                            actionType={ActionType.block}
                                        >
                                            {loadMoreLabel}
                                        </Action>
                                    </RowContentCell>
                                )}

                                {!hasNotEditableSiblings &&
                                    !hasEditableSiblings && (
                                        <RowContentCell data-ft-no-results>
                                            <FieldItems>
                                                <ReadOnly
                                                    value={noResultsLabel}
                                                    extraClasses="is-dimmed"
                                                />
                                            </FieldItems>
                                        </RowContentCell>
                                    )}
                            </TableBody>
                        </>
                    )}
                </Modal>
            </>
        )
    }

    showModal = (): void => this.setState({ show: true })

    hideModal = (): void => this.setState({ show: false })

    loadMore = (): void => {
        const { siblingsContent, loadMoreSiblings } = this.props
        if (!!siblingsContent) {
            loadMoreSiblings(siblingsContent[siblingsContent.length - 1].Id)
        }
    }
}
